<template>
  <main role="main">
    <b-container class="py-4">
      <div v-if="isLoading" class="py-5 text-center text-secondary">{{ $t("loadingData") }}</div>

      <template v-else>
        <section>
          <h4>{{ $t("accountInfo") }}</h4>
          <b-row>
            <b-col lg="6" class="mb-3 mb-lg-0">
              <b-row v-if="isManager()">
                <b-col cols="4"><span class="text-secondary">{{ $t("saleArea2") }}</span></b-col>
                <b-col cols="8">{{ saleArea.name }}</b-col>
              </b-row>
              <b-row>
                <b-col cols="4"><span class="text-secondary">{{ $t("accountId") }}:</span></b-col>
                <b-col cols="8">{{ user.id }}</b-col>
              </b-row>
              <b-row>
                <b-col cols="4"><span class="text-secondary">Username:</span></b-col>
                <b-col cols="8">{{ user.username }}</b-col>
              </b-row>
              <b-row>
                <b-col cols="4"><span class="text-secondary">{{ $t("fullName") }}:</span></b-col>
                <b-col cols="8" class="text-break">{{ user.name }}</b-col>
              </b-row>
              <b-row>
                <b-col cols="4"><span class="text-secondary">Email:</span></b-col>
                <b-col cols="8" class="text-break">{{ user.email }}</b-col>
              </b-row>
              <b-row>
                <b-col cols="4"><span class="text-secondary">{{ $t("phoneNumber") }}:</span></b-col>
                <b-col cols="8">{{ user.phone }}</b-col>
              </b-row>
              <b-row v-if="user.address">
                <b-col cols="4"><span class="text-secondary">{{ $t("address") }}:</span></b-col>
                <b-col cols="8" class="text-break">{{ user.address }}</b-col>
              </b-row>
            </b-col>
            <b-col lg="6" class="mb-3 mb-lg-0" v-if="appInfo">
              <b-row>
                <b-col cols="4"><span class="text-secondary">{{ $t("appName") }}</span></b-col>
                <b-col cols="8">{{ appInfo.name }}</b-col>
              </b-row>
              <b-row>
                <b-col cols="4"><span class="text-secondary">{{ $t("domain") }}</span></b-col>
                <b-col cols="8">{{ appInfo.domainName }}</b-col>
              </b-row>
              <b-row class="mt-3">
                <b-col cols="4"><span class="text-secondary">Logo</span></b-col>
                <b-col cols="8">
                  <div v-if="appInfo.logo" class="logo-container bg-primary mb-2">
                    <img :src="appInfo.logo" alt="logo">
                  </div>
                </b-col>
              </b-row>
            </b-col>
          </b-row>
          <div class="mt-3">
            <b-button variant="outline-primary" class="mr-3" v-b-modal:edit-personal-info>
              {{ $t("updateAccountInfo") }}
            </b-button>
            <b-button variant="outline-secondary" class="mr-3" to="/change-password">
              {{ $t("changePassword") }}
            </b-button>
            <b-button  v-if="!isUserAdmin() && !isManager()" variant="outline-danger" class="mr-3" @click="deleteMyAccount">
              {{ $t("deleteAccount") }}
            </b-button>
            <b-button v-if="appInfo" variant="outline-secondary" class="mr-3" v-b-modal:update-customer-app-modal>
              {{ $t("updateApp") }}
            </b-button>
          </div>
        </section>

        <section class="pt-4 mt-4 border-top" v-if="isCustomer()">
          <h4 class="mb-4">{{ $t("customerInfo") }}</h4>
          <b-row>
            <b-col lg="6" class="border-right">
              <b-row>
                <b-col cols="4"><span class="text-secondary">{{ $t("customerId") }}:</span></b-col>
                <b-col cols="8">{{ customer.customerCode }}</b-col>
              </b-row>
              <b-row>
                <b-col cols="4"><span class="text-secondary">{{ $t("customerName") }}:</span></b-col>
                <b-col cols="8">{{ customer.name }}</b-col>
              </b-row>
              <b-row>
                <b-col cols="4"><span class="text-secondary">{{ $t("customerType") }}:</span></b-col>
                <b-col cols="8">{{ customerTypeText }}</b-col>
              </b-row>
              <b-row v-if="customer.corporations">
                <b-col cols="4"><span class="text-secondary">{{ $t("underTheUnit") }}:</span></b-col>
                <b-col cols="8">{{ corporationsText }}</b-col>
              </b-row>
              <b-row v-else>
                <b-col cols="4"><span class="text-secondary">{{ $t("ownedBy") }}:</span></b-col>
                <b-col cols="8">{{ idTypeText }}</b-col>
              </b-row>
              <b-row>
                <b-col cols="4"><span class="text-secondary">Email:</span></b-col>
                <b-col cols="8">{{ customer.email }}</b-col>
              </b-row>
              <b-row>
                <b-col cols="4"><span class="text-secondary">{{ $t("phoneNumber") }}:</span></b-col>
                <b-col cols="8">{{ customer.phoneNumber }}</b-col>
              </b-row>
              <b-row>
                <b-col cols="4"><span class="text-secondary">{{ $t("province") }}:</span></b-col>
                <b-col cols="8">{{ customer.province }}</b-col>
              </b-row>
              <b-row>
                <b-col cols="4"><span class="text-secondary">{{ $t("address") }}:</span></b-col>
                <b-col cols="8">{{ customer.address }}</b-col>
              </b-row>
            </b-col>
            <b-col lg="6">
              <b-row v-if="customer.taxCode">
                <b-col cols="4"><span class="text-secondary">{{ $t("taxCode") }}:</span></b-col>
                <b-col cols="8">{{ customer.taxCode }}</b-col>
              </b-row>
              <b-row>
                <b-col cols="4"><span class="text-secondary">{{ $t("documentType") }}:</span></b-col>
                <b-col cols="8">{{ customer.documentTypeName }}</b-col>
              </b-row>
              <b-row>
                <b-col cols="4"><span class="text-secondary">{{ $t("documentNumber") }}:</span></b-col>
                <b-col cols="8">{{ customer.taxCode }}</b-col>
              </b-row>
              <b-row>
                <b-col cols="4"><span class="text-secondary">{{ $t("issueDate") }}:</span></b-col>
                <b-col cols="8">{{ localDateFormat(customer.licenseDate) }}</b-col>
              </b-row>
              <b-row>
                <b-col cols="4"><span class="text-secondary">{{ $t("expDate") }}:</span></b-col>
                <b-col cols="8">{{ localDateFormat(customer.licenseExpirationDate) }}</b-col>
              </b-row>
              <b-row>
                <b-col cols="4"><span class="text-secondary">{{ $t("issuePlace") }}:</span></b-col>
                <b-col cols="8">{{ customer.licensePlace }}</b-col>
              </b-row>
            </b-col>
          </b-row>
          <!-- <div class="mt-3">
            <b-button variant="outline-primary" class="mr-3" v-b-modal:edit-customer-info>
              {{ $t("updateCustomerInfo") }}
            </b-button>
          </div> -->
        </section>

       <section class="pt-4 mt-4 border-top" v-if="isCustomer()">
          <h4>{{ $t("registerServiceInfo") }}</h4>
          <div class="px-4 py-3 my-4 border rounded" v-if="activeCustomerSubscription">
            <b-row class="align-items-center mb-3">
              <b-col md="4">
                <h5>{{ activeSubscriptionPlan.name }}</h5>
                <div>{{ $t("displayActiveSubPrice", {
                  price: formatCurrencyAmount(activeSubscriptionPlan.price),
                  months: activeSubscriptionPlan.numberOfDaysUsage
                }) }}</div>
              </b-col>
              <b-col md="4">
                <div>
                  <b-icon icon="camera-video" class="mr-2"/>
                  {{ $t("displayActiveSubMeetingCount", { count: activeSubscriptionPlan.numberOfMeetingsPerMonth }) }}
                </div>
                <div>
                  <b-icon icon="blank" class="mr-2"/>
                  {{ $t("displayActiveSubConcurrentMeetings", { count: activeSubscriptionPlan.numberOfConcurrentMeetings }) }}
                </div>
              </b-col>
              <b-col md="4">
                <div>
                  <b-icon icon="people" class="mr-2"/>
                  {{ $t("displayActiveSubMaxParticipants", { count: activeSubscriptionPlan.maximumNumberOfParticipantsInMeeting }) }}
                </div>
                <div>
                  <b-icon icon="clock" class="mr-2"/>
                  {{ $t("displayActiveSubMaxMeetingLength", { count: activeSubscriptionPlan.maximumLengthOfOneMeeting }) }}
                </div>
              </b-col>
            </b-row>
            <b-row>
              <b-col md="4">
                <span class="text-secondary">{{ $t("subCreatedDate") }}:</span> {{ localDateFormat(activeCustomerSubscription.createdTime) }}
              </b-col>
              <b-col md="4">
                <span class="text-secondary">{{ $t("startDate") }}:</span> {{ localDateFormat(activeCustomerSubscription.startTime) }}
              </b-col>
              <b-col md="4">
                <span class="text-secondary">{{ $t("expDate") }}:</span> {{ localDateFormat(activeCustomerSubscription.endTime) }}
              </b-col>
            </b-row>
          </div>
          <div v-else class="text-secondary">{{ $t("noActiveSub") }}</div>
        </section>

        <section class="pt-4 mt-4 border-top" v-if="isCustomer()">
          <h4 class="mb-3">{{ $t("storageInfo") }}</h4>
          <b-progress class="mb-2" :value="storageInfo.storageUsed" :max="storageInfo.storagePlan"></b-progress>
          <div>
            {{ $t("displayStorageUsed", { used: storageInfo.storageUsed.toFixed(0), total: storageInfo.storagePlan.toFixed(0) }) }}
          </div>
        </section>

      </template>

    </b-container>

    <b-modal id="edit-personal-info" :title="$t('updateAccountInfo')" hide-footer no-close-on-backdrop>
      <EditPersonalInfoModal modal-id="edit-personal-info" />
    </b-modal>

    <!-- <b-modal id="edit-customer-info" :title="$t('updateCustomerInfo')" hide-footer no-close-on-backdrop>
      <EditCustomerInfoModal modal-id="edit-customer-info" />
    </b-modal> -->

    <b-modal id="change-password-modal" :title="$t('changePassword')" hide-footer no-close-on-backdrop>
      <ChangePasswordModal modal-id="change-password-modal" />
    </b-modal>

    <b-modal id="update-customer-app-modal" :title="$t('updateApp')" hide-footer no-close-on-backdrop>
      <UpdateCustomerAppModal modal-id="update-customer-app-modal" :item="appInfo" @submitted="onAppInfoChanged" />
    </b-modal>

  </main>
</template>

<style lang="scss" scoped>
  .logo-container {
    flex-shrink: 0;
    width: 50px; height: 50px;
    display: grid; place-items: center;

    > img {
      width: 28px;
    }
  }
</style>

<script lang="ts">
  import {Component, Vue} from 'vue-property-decorator';
  import _ from "lodash";
  import BaseComponent from "@/components/BaseComponent";

  import * as areaService from "@/service/areaService";
  import * as customerService from "@/service/customerService";
  import * as subscriptionPlanService from "@/service/subscriptionPlanService";
  import * as appNamesService from "@/service/appNamesService";
  import ChangePasswordModal from "@/components/account/ChangePasswordModal.vue";
  import EditPersonalInfoModal from "@/components/account/EditPersonalInfoModal.vue";
  import UpdateCustomerAppModal from "@/components/app-names/UpdateCustomerAppModal.vue";
  import * as meetingService from "@/service/meetingService";
  import * as userService from "@/service/userService";
  import environment from "@/environment";

  @Component({
    components: { ChangePasswordModal, EditPersonalInfoModal, UpdateCustomerAppModal }
  })
  export default class ManageAccount extends BaseComponent {

    readonly roleText = {
      Customer: "Quản trị khách hàng",
      Manager: "Quản trị TTKD",
      Admin: "Quản trị dịch vụ",
      SupperAdmin: "Quản trị dịch vụ",
    };

    isLoading = true;
    saleArea = null;

    customer = null;
    appInfo = null;
    activeCustomerSubscription = null;
    activeSubscriptionPlan = null;
    storageInfo = null;


    mounted() {
      this.fetchUser();
      if (this.isCustomer()) {
        this.fetchAppInfo();
      }
    }

    async fetchUser() {
      try {
        this.isLoading = true;

        if (this.isManager()) {
          const saleAreas = await areaService.getAll();
          this.saleArea = saleAreas.find(it => it.id === this.user.saleAreaId);

        } else if (this.isCustomer()) {
          const { customerId } = this.user;
          const fetchCustomerPromise = customerService.getDetails(customerId);
          const fetchDocumentTypesPromise = customerService.getDocumentTypes();
          const fetchStorageInfoPromise = meetingService.getStoragePlan();

          const customerSubscriptions = await subscriptionPlanService.getCustomerSubscription(customerId);
          this.activeCustomerSubscription = customerSubscriptions.find(it => it.isActive);

          if (this.activeCustomerSubscription) {
            const { subscriptionPlanId } = this.activeCustomerSubscription;
            this.activeSubscriptionPlan = await subscriptionPlanService.getPackageDetail(subscriptionPlanId);
          }
          this.customer = await fetchCustomerPromise;
          const documentTypes = await fetchDocumentTypesPromise;
          this.customer.documentTypeName = documentTypes.find(it => it.id === this.customer.documentTypeId)?.title;
          this.storageInfo = await fetchStorageInfoPromise;
        }

      } catch (e) {
        this.toastError(e);

      } finally {
        this.isLoading = false;
      }
    }


    async fetchAppInfo() {
      try {
        this.appInfo = await appNamesService.getCustomerApp();
      } catch (e) {
        console.error(e);
      }
    }

    onAppInfoChanged() {
      location.reload();
    }

    async deleteMyAccount() {
      const modalMessage = this.$t("deleteYourAccount").toString();
      const confirmDelete = await this.$bvModal.msgBoxConfirm(modalMessage, {
        okVariant: 'danger',
        okTitle: this.$t("yes").toString(),
        cancelTitle: this.$t("no").toString(),
      });

      if (confirmDelete) {
        try {
          this.showLoading();
          await userService.deleteUser(this.userId).then((data)=>{
            userService.signOut();
          });
        } catch (e) {
          this.toastError(e);

        } finally {
          this.hideLoading();
        }
      }
    }

    get customerTypeText(): string {
      const type = this.customer?.customerType?.toLowerCase();
      return  this.$t(type).toString();
    }
    get corporationsText(): string {
      const type = this.customer?.corporations?.toLowerCase();
      return  this.$t(type).toString();
    }
    get idTypeText(): string {
      const type = this.customer?.idType?.toLowerCase();
      return  this.$t(type).toString();
    }

  }
</script>
